import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, 
  ArrowLeft,
  Calendar,
  Send,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Edit,
  Save
} from 'lucide-react';
import { receiptsAPI, profileAPI } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Custom Receipt Icon with Rupee symbol
const ReceiptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M13 16H8" />
    <text x="15.5" y="9.5" fontSize="7" fontWeight="bold" fill="currentColor" textAnchor="middle">₹</text>
  </svg>
);

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  placeOfSupply: string;
}

interface InvoiceItem {
  serialNumber: number;
  description: string;
  sac: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: number;
}

interface ReceiptData {
  _id: string;
  receiptNumber: string;
  receiptDate: string;
  invoice?: Invoice;
  invoiceNumber: string;
  client: Client;
  items?: InvoiceItem[];
  amount: number;
  amountInWords: string;
  subtotal?: number;
  taxableAmount?: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  paymentMode: string;
  paymentDetails: {
    referenceNumber?: string;
    bankName?: string;
    chequeNumber?: string;
    transactionDate?: string;
  };
  notes?: string;
  isManual: boolean;
  createdAt: string;
  tdsDeducted?: number;
  tdsPercentage?: number;
}

interface CompanyProfile {
  companyName: string;
  logo?: {
    url: string;
    filename?: string;
  };
  digitalSignature?: {
    url: string;
    filename?: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    ifscCode: string;
    branch: string;
  };
  gstNumber: string;
  email: string;
  contactNumber: string;
  receiptTermsAndConditions?: string;
}

const ReceiptDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isEditingTDS, setIsEditingTDS] = useState(false);
  const [tdsPercentage, setTdsPercentage] = useState<number>(0);
  const [tdsDeducted, setTdsDeducted] = useState<number>(0);
  const [updatingTDS, setUpdatingTDS] = useState(false);

  const fetchReceipt = useCallback(async () => {
    if (!id) return;
    
    try {
      const [receiptRes, profileRes] = await Promise.all([
        receiptsAPI.getById(id),
        profileAPI.get().catch(() => null)
      ]);
      
      setReceipt(receiptRes.data);
      setTdsPercentage(receiptRes.data.tdsPercentage || 0);
      setTdsDeducted(receiptRes.data.tdsDeducted || 0);
      if (profileRes?.data) {
        setCompanyProfile(profileRes.data);
      }
    } catch (error) {
      console.error('Error fetching receipt:', error);
      setMessage({ type: 'error', text: 'Failed to load receipt' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  const handleGeneratePDF = async () => {
    if (!receipt) return;
    
    setGeneratingPDF(true);
    try {
      const element = document.getElementById('receipt-content');
      if (!element) return;

      // Hide edit buttons and TDS edit form before generating PDF
      const editButtons = element.querySelectorAll('.hide-in-pdf');
      editButtons.forEach((btn: any) => {
        btn.style.display = 'none';
      });

      const canvas = await html2canvas(element, {
        scale: 2, // High resolution for crisp text and sharp images when zoomed
        useCORS: true,
        allowTaint: true,
        logging: false, // Disable logging for better performance
        backgroundColor: '#ffffff',
      });
      
      // Show edit buttons back after PDF generation
      editButtons.forEach((btn: any) => {
        btn.style.display = '';
      });
      
      // Use JPEG format with quality 0.95 for high quality while still reducing file size compared to PNG
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Use JPEG format instead of PNG for better compression
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`Receipt-${receipt.receiptNumber}.pdf`);
      setMessage({ type: 'success', text: 'PDF generated successfully!' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setMessage({ type: 'error', text: 'Failed to generate PDF' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleSendEmail = async () => {
    if (!receipt) return;
    
    setSendingEmail(true);
    setMessage(null);
    
    try {
      await receiptsAPI.sendEmail(receipt._id);
      setMessage({ type: 'success', text: 'Receipt sent successfully!' });
    } catch (error) {
      console.error('Error sending email:', error);
      setMessage({ type: 'error', text: 'Failed to send receipt email' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async () => {
    if (!receipt) return;
    
    setDeleting(true);
    setMessage(null);
    
    try {
      await receiptsAPI.delete(receipt._id);
      setMessage({ type: 'success', text: 'Receipt deleted successfully!' });
      setTimeout(() => {
        navigate('/receipts');
      }, 1500);
    } catch (error) {
      console.error('Error deleting receipt:', error);
      setMessage({ type: 'error', text: 'Failed to delete receipt' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUpdateTDS = async () => {
    if (!receipt) return;
    
    setUpdatingTDS(true);
    setMessage(null);
    
    try {
      // Calculate TDS deducted from percentage
      // TDS should be calculated on subtotal (gross amount) not on total amount (which includes tax)
      const baseAmount = (receipt.subtotal && receipt.subtotal > 0) ? receipt.subtotal : receipt.amount;
      const calculatedTDS = (baseAmount * tdsPercentage) / 100;
      const roundedTDS = Math.round(calculatedTDS * 100) / 100;
      
      await receiptsAPI.update(receipt._id, {
        tdsPercentage,
        tdsDeducted: roundedTDS
      });
      
      setReceipt({ ...receipt, tdsPercentage, tdsDeducted: roundedTDS });
      setTdsDeducted(roundedTDS);
      setIsEditingTDS(false);
      setMessage({ type: 'success', text: 'TDS updated successfully!' });
    } catch (error) {
      console.error('Error updating TDS:', error);
      setMessage({ type: 'error', text: 'Failed to update TDS' });
    } finally {
      setUpdatingTDS(false);
    }
  };

  const handleTdsPercentageChange = (value: string) => {
    const percentage = parseFloat(value) || 0;
    setTdsPercentage(percentage);
    // TDS should be calculated on subtotal (gross amount) not on total amount (which includes tax)
    const baseAmount = (receipt && receipt.subtotal && receipt.subtotal > 0) ? receipt.subtotal : (receipt ? receipt.amount : 0);
    const calculated = baseAmount * percentage / 100;
    setTdsDeducted(Math.round(calculated * 100) / 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const numberToWords = (num: number) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    function convertHundreds(n: number): string {
      let result = '';
      
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result.trim();
      }
      
      if (n > 0) {
        result += ones[n] + ' ';
      }
      
      return result.trim();
    }
    
    let result = '';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = num % 1000;
    
    if (crore > 0) {
      result += convertHundreds(crore) + ' Crore ';
    }
    if (lakh > 0) {
      result += convertHundreds(lakh) + ' Lakh ';
    }
    if (thousand > 0) {
      result += convertHundreds(thousand) + ' Thousand ';
    }
    if (hundred > 0) {
      result += convertHundreds(hundred) + ' ';
    }
    
    return result.trim() + ' Rupees Only';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="text-center py-8">
        <ReceiptIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Receipt not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-3">
            <ReceiptIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{receipt.receiptNumber}</h1>
              <p className="text-gray-600">Receipt Details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4"
      >
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Created: {formatDate(receipt.createdAt)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Receipt Date: {formatDate(receipt.receiptDate)}</span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="btn-secondary flex items-center space-x-2"
          >
            <Send className="h-4 w-4" />
            <span>{sendingEmail ? 'Sending...' : 'Send Email'}</span>
          </button>
          <button
            onClick={handleGeneratePDF}
            disabled={generatingPDF}
            className="btn-primary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{generatingPDF ? 'Generating...' : 'Download PDF'}</span>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-secondary text-red-600 hover:bg-red-50 flex items-center space-x-2 border-red-200"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </motion.div>

      {/* Receipt Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        id="receipt-content"
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
      >
        {/* Company Logo and Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-start">
          <div>
            {companyProfile?.logo?.url && (
              <img 
                src={companyProfile.logo.url} 
                alt="Company Logo" 
                className="h-16 mb-4 object-contain"
              />
            )}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-12">From:</h3>
            <div className="space-y-1">
              <p className="font-medium text-gray-900">{companyProfile?.companyName}</p>
              <p className="text-gray-600">{companyProfile?.email}</p>
              {companyProfile?.contactNumber && <p className="text-gray-600">{companyProfile?.contactNumber}</p>}
              {companyProfile && (
                <div className="text-gray-600">
                  <p>{companyProfile.address.street}</p>
                  <p>{companyProfile.address.city}, {companyProfile.address.state} - {companyProfile.address.pincode}</p>
                  <p>{companyProfile.address.country}</p>
                </div>
              )}
              <p className="text-gray-600">GSTIN: {companyProfile?.gstNumber}</p>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">PAYMENT RECEIPT</h2>
            <div className="space-y-1 mb-6">
              <p className="text-gray-600">Receipt No: {receipt.receiptNumber}</p>
              <p className="text-gray-600">Date: {formatDate(receipt.receiptDate)}</p>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">
                {receipt.tdsDeducted && receipt.tdsDeducted > 0 ? 'Net Amount Received' : 'Amount Received'}
              </div>
              <div className="text-2xl font-bold text-primary-600">
                {formatCurrency(Math.round((receipt.tdsDeducted && receipt.tdsDeducted > 0) ? receipt.amount - (receipt.tdsDeducted || 0) : receipt.amount))}
              </div>
            </div>
          </div>
        </div>

        {/* Client Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Received From:</h3>
            <div className="space-y-1">
              <p className="font-medium text-gray-900">{receipt.client.clientName}</p>
              <p className="text-gray-600">{receipt.client.email}</p>
              {receipt.client.contactNumber && <p className="text-gray-600">{receipt.client.contactNumber}</p>}
              <div className="text-gray-600">
                <p>{receipt.client.address.street}</p>
                <p>{receipt.client.address.city}, {receipt.client.address.state} - {receipt.client.address.pincode}</p>
                <p>{receipt.client.address.country}</p>
              </div>
              {receipt.client.gstin && (
                <p className="text-gray-600">GSTIN: {receipt.client.gstin}</p>
              )}
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details:</h3>
            <div className="space-y-1 text-gray-600">
              <p><strong>Invoice Number:</strong> {receipt.invoiceNumber}</p>
              <p><strong>Payment Mode:</strong> {receipt.paymentMode}</p>
              {receipt.paymentDetails?.referenceNumber && (
                <p><strong>Reference Number:</strong> {receipt.paymentDetails.referenceNumber}</p>
              )}
              {receipt.paymentDetails?.transactionDate && (
                <p><strong>Transaction Date:</strong> {formatDate(receipt.paymentDetails.transactionDate)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Receipt Items - Show if items exist */}
        {receipt.items && receipt.items.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Goods/Services Description:</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SAC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {receipt.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.serialNumber}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sac || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.rate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Amount Section */}
        <div className="mb-8 p-4 bg-primary-50 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Payment Summary</h3>
            <button
              onClick={() => {
                if (isEditingTDS) {
                  setIsEditingTDS(false);
                } else {
                  setIsEditingTDS(true);
                }
              }}
              className="hide-in-pdf text-primary-600 hover:text-primary-700 p-2 hover:bg-primary-100 rounded-lg transition-colors"
              title="Edit TDS"
            >
              <Edit className="h-5 w-5" />
            </button>
          </div>

          {isEditingTDS ? (
            <div className="hide-in-pdf space-y-4">
              {/* Tax Breakdown - Show if tax info exists */}
              {(() => {
                // Handle string "00" or "0" cases by converting to number
                let subtotalValue = 0;
                if (receipt.subtotal !== undefined && receipt.subtotal !== null) {
                  if (typeof receipt.subtotal === 'number') {
                    subtotalValue = receipt.subtotal;
                  } else if (typeof receipt.subtotal === 'string') {
                    const parsed = parseFloat(receipt.subtotal);
                    subtotalValue = isNaN(parsed) ? 0 : parsed;
                  } else {
                    subtotalValue = Number(receipt.subtotal) || 0;
                  }
                }
                const cgst = receipt.cgstAmount ?? 0;
                const sgst = receipt.sgstAmount ?? 0;
                const igst = receipt.igstAmount ?? 0;
                return subtotalValue > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Tax Breakdown:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Subtotal:</span>
                        <span className="text-gray-900">{formatCurrency(subtotalValue)}</span>
                      </div>
                    {cgst > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">CGST ({receipt.cgstRate || 0}%):</span>
                        <span className="text-gray-900">{formatCurrency(cgst)}</span>
                      </div>
                    )}
                    {sgst > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">SGST ({receipt.sgstRate || 0}%):</span>
                        <span className="text-gray-900">{formatCurrency(sgst)}</span>
                      </div>
                    )}
                    {igst > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">IGST ({receipt.igstRate || 0}%):</span>
                        <span className="text-gray-900">{formatCurrency(igst)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-300">
                      <span className="font-semibold text-gray-900">Total Amount:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(receipt.amount)}</span>
                    </div>
                  </div>
                </div>
              );
              })()}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gross Amount</label>
                  <input
                    type="text"
                    value={formatCurrency(receipt.amount)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS Percentage (%)</label>
                  <input
                    type="number"
                    value={tdsPercentage}
                    onChange={(e) => handleTdsPercentageChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g., 10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS Deducted (Auto-calculated)</label>
                  <input
                    type="text"
                    value={formatCurrency(tdsDeducted)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Net Amount Received</label>
                  <input
                    type="text"
                    value={formatCurrency(Math.round(receipt.amount - tdsDeducted))}
                    readOnly
                    className="w-full px-3 py-2 border border-primary-300 rounded-lg bg-primary-50 text-primary-700 font-bold"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-primary-200">
                <button
                  onClick={() => setIsEditingTDS(false)}
                  disabled={updatingTDS}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTDS}
                  disabled={updatingTDS}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{updatingTDS ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          ) : (
            (() => {
              // Prepare tax amounts with safe defaults
              const cgst = receipt.cgstAmount ?? 0;
              const sgst = receipt.sgstAmount ?? 0;
              const igst = receipt.igstAmount ?? 0;
              // Check if any tax amount exists and is greater than 0
              const hasTaxAmount = cgst > 0 || sgst > 0 || igst > 0;
              // Convert subtotal to number and check if it's valid and > 0
              // Handle string "00" or "0" cases by converting to number
              let subtotalValue = 0;
              if (receipt.subtotal !== undefined && receipt.subtotal !== null) {
                if (typeof receipt.subtotal === 'number') {
                  subtotalValue = receipt.subtotal;
                } else if (typeof receipt.subtotal === 'string') {
                  const parsed = parseFloat(receipt.subtotal);
                  subtotalValue = isNaN(parsed) ? 0 : parsed;
                } else {
                  subtotalValue = Number(receipt.subtotal) || 0;
                }
              }
              // Only show tax breakdown if subtotal > 0 AND at least one tax amount > 0 AND total amount > 0
              const hasTaxInfo = hasTaxAmount && 
                                  subtotalValue > 0 && 
                                  receipt.amount > 0;
              const tdsValue = receipt.tdsDeducted ?? 0;
              const hasTDS = tdsValue > 0;
              
              return (
                <div className="space-y-4">
                  {/* Tax Breakdown - Show ONLY if subtotal > 0 AND tax amounts exist AND total amount > 0 */}
                  {hasTaxInfo && (
                    <>
                      {subtotalValue > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Subtotal:</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotalValue)}</span>
                        </div>
                      )}
                      {cgst > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">CGST ({receipt.cgstRate || 0}%):</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(cgst)}</span>
                        </div>
                      )}
                      {sgst > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">SGST ({receipt.sgstRate || 0}%):</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(sgst)}</span>
                        </div>
                      )}
                      {igst > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">IGST ({receipt.igstRate || 0}%):</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(igst)}</span>
                        </div>
                      )}
                      {/* Show Total Amount here only if TDS exists (so we show: Tax breakdown -> Total Amount -> TDS -> Net Amount) */}
                      {hasTDS && receipt.amount > 0 && (
                        <div className="flex justify-between items-center border-t border-gray-300 pt-3 mb-2">
                          <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                          <span className="text-xl font-bold text-gray-900">{formatCurrency(receipt.amount)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* TDS Section - Show if TDS exists */}
                  {hasTDS ? (
                    <div className="flex justify-between items-center border-t border-primary-200 pt-4">
                      <span className="text-sm font-semibold text-gray-700">
                        TDS Deducted ({receipt.tdsPercentage || 0}%):
                      </span>
                      <span className="text-base font-medium text-gray-900">
                        -{formatCurrency(tdsValue)}
                      </span>
                    </div>
                  ) : null}
                  
                  {/* Final Amount - Show ONE Total Amount if no TDS, or Net Amount if TDS exists */}
                  <div className="flex justify-between items-center border-t border-primary-300 pt-4">
                    <span className="text-lg font-semibold text-gray-900">
                      {hasTDS ? 'Net Amount Received:' : hasTaxInfo ? 'Total Amount:' : 'Amount Received:'}
                    </span>
                    <span className="text-2xl font-bold text-primary-600">
                      {formatCurrency(Math.round(hasTDS ? receipt.amount - (receipt.tdsDeducted || 0) : receipt.amount))}
                    </span>
                  </div>
                  
                  {/* Amount in Words */}
                  <div className="border-t border-primary-200 pt-4">
                    <p className="text-sm text-gray-600">
                      <strong>Amount in Words:</strong> {
                        hasTDS 
                          ? numberToWords(Math.round(receipt.amount - (receipt.tdsDeducted || 0))) + ' Only'
                          : receipt.amountInWords
                      }
                    </p>
                  </div>
                </div>
              );
            })()
          )}
        </div>

        {/* Notes */}
        {receipt.notes && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes:</h3>
            <p className="text-gray-600">{receipt.notes}</p>
          </div>
        )}

        {/* Terms and Conditions */}
        {companyProfile?.receiptTermsAndConditions && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Terms & Conditions:</h3>
            <p className="text-sm text-gray-600">
              {companyProfile.receiptTermsAndConditions}
            </p>
          </div>
        )}

        {/* Signature */}
        <div className="mt-8 flex justify-end">
          <div className="text-center">
            {companyProfile?.digitalSignature?.url && (
              <div className="mb-4">
                <img 
                  src={companyProfile.digitalSignature.url} 
                  alt="Digital Signature" 
                  className="h-16 object-contain mx-auto"
                />
              </div>
            )}
            <p className="text-sm font-semibold text-gray-900">{companyProfile?.companyName || ''}</p>
            <p className="text-xs text-gray-500 mt-1">
              Authorized Signatory
            </p>
          </div>
        </div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Receipt</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
                <div>
                  <p className="text-gray-900 font-medium">Are you sure you want to delete this receipt?</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone. This will permanently delete receipt {receipt.receiptNumber}.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-primary bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Delete Receipt'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ReceiptDetails;

